import * as React from 'react';
import { useEffect, useCallback } from "react";
import {
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    ExpandableRowContent,
} from '@patternfly/react-table';
import { useSortableData } from '@app/hooks';
import {
    Button,
    Popover,
} from '@patternfly/react-core';

import { SearchIcon, RunningIcon, AsleepIcon, InProgressIcon, OutlinedClockIcon, OutlinedQuestionCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, WrenchIcon } from '@patternfly/react-icons'
import Timestamp from 'react-timestamp';
import { NamespaceDetails } from './NamespaceDetails';
import { Namespace } from '@app/Dashboard/models';

interface NamespaceTableProps {
    namespaces: Namespace[];
    clearFilters: CallableFunction;
}

const NamespacesTable: React.FunctionComponent<NamespaceTableProps> = ({ namespaces, clearFilters }) => {

    const columnNames = {
        name: 'Name',
        creator: 'Creator',
        team: 'Team',
        date: 'Last modified',
        status: 'Status'
    };

    /* Namespace expanding management */
    const [expandedNamespaces, setExpandedNamespaces] = React.useState<string[]>([]);

    useEffect(() => {
        let savedExpandedNamespaces = sessionStorage.getItem('expandedNamespaces');
        if (savedExpandedNamespaces != undefined) {
            let namespaces = JSON.parse(savedExpandedNamespaces);
            setExpandedNamespaces(namespaces);
        }
    }, [])


    const isNamespaceExpanded = (namespace: Namespace) => expandedNamespaces.includes(namespace.name);
    const setNamespaceExpanded = (namespace: Namespace, isExpanding = true) =>
        setExpandedNamespaces((prevExpanded) => {
            const otherExapandedNamespaces = prevExpanded.filter((r) => r !== namespace.name);
            const result = isExpanding ? [...otherExapandedNamespaces, namespace.name] : otherExapandedNamespaces;

            sessionStorage.setItem('expandedNamespaces', JSON.stringify(result));

            return result;
        });

    const labels = {
        running: { text: 'Running', icon: <RunningIcon />, color: 'green', severity: 'success', description: 'The environment seems to be healthy.' },
        stale: { text: 'Stale', icon: <ExclamationTriangleIcon />, color: 'gold', description: 'There are deployed applications missing changes from their repository main branch, expand the namespace row for more details.' },
        outdated: { text: 'Outdated', icon: <ExclamationTriangleIcon />, color: 'gold', severity: 'warning', description: 'There are outdated applications, expand the namespace row for more details.' },
        stopped: { text: 'Stopped', icon: <AsleepIcon />, color: 'grey', severity: 'custom', description: 'The environment is paused. Use wake up job to resume.' },
        updating: { text: 'Updating', icon: <InProgressIcon />, color: 'blue', severity: 'info', description: 'There is at least one application updating.' },
        failing: { text: 'Failing', icon: <ExclamationCircleIcon />, color: 'red', severity: 'danger', description: 'There is at least one application failing, expand the namespace row for more details.' },
        warning: { text: 'Warning', icon: <ExclamationTriangleIcon />, color: 'orange', severity: 'warning', description: 'There is at least one unstable application, expand the namespace row for more details.' },
        patching: { text: 'Patching', icon: <WrenchIcon />, color: 'purple', severity: 'info', description: 'There is one failing application being updated.' },
        unknown: { text: 'Unknown', icon: <OutlinedQuestionCircleIcon />, color: 'cyan', severity: 'info', description: 'Cannot determine the status of the namespace. Expand it to get more details.' },
        idle: { text: 'Idle', icon: <OutlinedClockIcon />, color: 'grey' as const, variant: 'outline' as const, severity: 'info' as const, description: 'The namespace has not received any request in the last period' }
    }

    const renderStatusLabel = (status, count) => {
        const values = labels[status];

        return (
            <Popover
                triggerAction="hover"
                position='left'
                alertSeverityVariant={values.severity}
                headerIcon={values.icon}
                aria-label="Hoverable popover"
                headerContent={values.text}
                enableFlip={true}
                bodyContent={
                    <div>
                        {values.description}
                    </div>
                }
                footerContent={undefined}
            >
                <span className={`dx-label dx-label--${values.color}${values.variant === 'outline' ? ' dx-label--outline' : ''}`}>
                    <span className="dx-label__icon">{values.icon}</span>
                    {count ? count + ' ' : undefined}{values.text}
                </span>
            </Popover>)

            ;
    }

    let namespaceStatusCount = new Map(namespaces.map(namespace => {
        let count;
        if (namespace.status == 'updating') {
            count = namespace.resources.filter(resource => ['creating', 'initializing', 'updating'].includes(resource.status)).length;
        } else if (namespace.status == 'unknonw' || namespace.status == 'stopped') {
            count = undefined
        } else {
            count = namespace.resources.filter(resource => resource.status == namespace.status).length
        }
        return [namespace.name, count]
    }))

    const getRowValues = useCallback(
        (namespace: Namespace) => [namespace.name, namespace.creator, namespace.team, namespace.date.getTime(), namespace.status],
        []
    );

    const { sortedData: sortedNamespaces, getSortParams } = useSortableData(namespaces, getRowValues);

    return (
        <Table variant='compact'>
            <Thead>
                <Tr>
                    <Th />
                    <Th sort={getSortParams(0)}>{columnNames.name}</Th>
                    <Th sort={getSortParams(1)}>{columnNames.creator}</Th>
                    <Th sort={getSortParams(2)}>{columnNames.team}</Th>
                    <Th sort={getSortParams(3)}>{columnNames.date}</Th>
                    <Th sort={getSortParams(4)}>{columnNames.status}</Th>
                </Tr>
            </Thead>
            {namespaces.length ? (
                sortedNamespaces.map((namespace, rowIndex) => (
                    <Tbody key={namespace.name}>
                        <Tr>
                            <Td
                                expand={
                                    {
                                        rowIndex,
                                        isExpanded: isNamespaceExpanded(namespace),
                                        onToggle: () => setNamespaceExpanded(namespace, !isNamespaceExpanded(namespace)),
                                        expandId: 'composable-expandable-example'
                                    }
                                }
                            />
                            <Td dataLabel={columnNames.name}>
                                <div className="dx-flex dx-flex--gap-sm">
                                    <div>{namespace.name}</div>
                                    {namespace.idle == true ?
                                        <div>
                                            <Popover
                                                triggerAction="hover"
                                                position='right'
                                                alertSeverityVariant={labels.idle.severity}
                                                headerIcon={labels.idle.icon}
                                                aria-label="Hoverable popover"
                                                headerContent={labels.idle.text}
                                                enableFlip={true}
                                                bodyContent={
                                                    <div>
                                                        The environment has not received any request in the lasts {namespace.requests.period}
                                                    </div>
                                                }
                                                footerContent={undefined}

                                            >
                                                <span className="dx-label dx-label--compact dx-label--grey">
                                                    <span className="dx-label__icon">{labels.idle.icon}</span>
                                                    Idle
                                                </span>
                                            </Popover>
                                        </div>
                                        : undefined}
                                </div>
                            </Td>
                            <Td dataLabel={columnNames.creator}>{namespace.creator}</Td>
                            <Td dataLabel={columnNames.team}>{namespace.team}</Td>
                            <Td dataLabel={columnNames.date}>
                                {namespace.date ? <Timestamp date={new Date(namespace.date)} relative /> : undefined}
                            </Td>
                            <Td dataLabel={columnNames.status}>
                                {renderStatusLabel(namespace.status, namespaceStatusCount.get(namespace.name))}
                            </Td>
                        </Tr>

                        {isNamespaceExpanded(namespace) ? (
                            <Tr isExpanded={isNamespaceExpanded(namespace)} isStriped>
                                <Td
                                    dataLabel={`${columnNames.name} expended`}
                                    colSpan={Object.keys(columnNames).length + 1}
                                >
                                    <ExpandableRowContent>
                                        <NamespaceDetails namespace={namespace} />
                                    </ExpandableRowContent>
                                </Td>
                            </Tr>
                        ) : undefined}
                    </Tbody>
                ))
            ) :
                (
                    <Tbody>
                        <Tr>
                            <Td colSpan={8}>
                                <div className="dx-bullseye">
                                    <div className="dx-empty-state dx-empty-state--sm">
                                        <div className="dx-empty-state__icon"><SearchIcon /></div>
                                        <h2 className="dx-empty-state__title">No results found</h2>
                                        <div className="dx-empty-state__body">
                                            No results match this filter criteria. Clear all filters and try again.
                                        </div>
                                        <div className="dx-empty-state__footer">
                                            <Button variant="link" onClick={() => clearFilters()}>Clear all filters</Button>
                                        </div>
                                    </div>
                                </div>
                            </Td>
                        </Tr>
                    </Tbody>)}
        </Table>
    )
}


export { NamespacesTable, NamespaceTableProps }